import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ActivityFeedCard = ({ activities, currentUserName }) => {
  const getActivityIcon = (type) => {
    const icons = {
      'volunteer': 'UserCheck',
      'event': 'Calendar',
      'achievement': 'Award',
      'organization': 'Building2',
      'skill': 'GraduationCap'
    };
    return icons?.[type] || 'Activity';
  };

  const getActivityColor = (type) => {
    const colors = {
      'volunteer': 'text-blue-600 bg-blue-50',
      'event': 'text-green-600 bg-green-50',
      'achievement': 'text-amber-600 bg-amber-50',
      'organization': 'text-purple-600 bg-purple-50',
      'skill': 'text-red-600 bg-red-50'
    };
    return colors?.[type] || 'text-gray-600 bg-gray-50';
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const isCurrentUser = (activity) => {
    if (!currentUserName) return false;
    const aName = (activity?.userName || '').toLowerCase().replace(/\s+/g, '');
    const cName = (currentUserName || '').toLowerCase().replace(/\s+/g, '');
    if (!aName || !cName) return false;
    return aName === cName || aName.includes(cName) || cName.includes(aName);
  };

  const userActivities = (activities || []).filter(isCurrentUser);

  return (
    <div className="bg-card rounded-xl p-6 shadow-md border border-border">
      <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
        <Icon name="Activity" size={24} className="text-accent" />
        Recent Activity
      </h2>
      <div className="space-y-4 max-h-96 overflow-y-auto custom-scrollbar">
        {userActivities?.length > 0 && (
          <div className="mb-3 p-3 bg-primary/5 rounded-md">
            <h3 className="text-sm font-semibold">Your Recent Activity</h3>
            <div className="mt-2 space-y-1 text-sm text-muted-foreground">
              {userActivities.slice(0,3).map((act) => (
                <div key={act.id} className="flex justify-between">
                  <div>{act.action} <span className="font-medium">{act.details}</span></div>
                  <div className="text-xs">{getTimeAgo(act.timestamp)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {activities?.map((activity) => (
          <div
            key={activity?.id}
            className={`flex gap-3 p-3 rounded-lg hover:bg-muted transition-colors ${isCurrentUser(activity) ? 'bg-primary/5 border-l-4 border-primary/20' : ''}`}
          >
            <div className="flex-shrink-0">
              {activity?.userAvatar ? (
                <div className="w-10 h-10 rounded-full overflow-hidden bg-muted">
                  <Image
                    src={activity?.userAvatar}
                    alt={activity?.userAvatarAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getActivityColor(activity?.type)}`}>
                  <Icon name={getActivityIcon(activity?.type)} size={20} />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground mb-1">
                <span className="font-semibold">{activity?.userName}</span>
                {isCurrentUser(activity) && (
                  <span className="ml-2 inline-block text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">You</span>
                )}
                {' '}
                <span className="text-muted-foreground">{activity?.action}</span>
              </p>
              {activity?.details && (
                <p className="text-sm text-muted-foreground mb-1 line-clamp-2">
                  {activity?.details}
                </p>
              )}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {getTimeAgo(activity?.timestamp)}
                </span>
                {activity?.badge && (
                  <span className="text-xs font-medium text-accent bg-accent/10 px-2 py-0.5 rounded-full">
                    {activity?.badge}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivityFeedCard;